import { useEffect, useState } from 'react';
import { includes, isEmpty } from 'lodash';
import { UsersContextType } from '@/operator/components/users/context/UsersContext';
import {
    FirestoreUser,
    Invitation,
    useGetInvitesQuery,
    useGetUsersQuery,
} from 'generated/graphql';
import { useAppSelector } from 'hooks/index';
import { selectPartnerData } from 'store/partner/partnerSelector';
import _sortBy from "lodash/sortBy";

const useUsersTable = (): UsersContextType => {
    const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState<Invitation[]>();

    const [usersTable, setUsersTable] = useState<FirestoreUser[]>();
    const [invitationsTable, setInvitationsTable] = useState<Invitation[]>();

    const [searchInput, setSearchInput] = useState<string>('');
    const [searchFilters, setSearchFilters] = useState([]);

    const [selectedInvitationType, setSelectedInvitationType] = useState<string>();

    const { instanceId } = useAppSelector(selectPartnerData);

    const { data: queryUsers, loading: loadingUsers } = useGetUsersQuery({
        variables: {
            instanceId,
        },
    });

    const { data: queryInvites, loading: loadingInvites } = useGetInvitesQuery({
        variables: {
            organizationId: instanceId,
        },
    });

    const usersResults = queryUsers?.users || [];
    const invitesResults = queryInvites?.invitations || [];

    useEffect(() => {
        setUsers(_sortBy(usersResults, ['displayName', 'email']));
    }, [usersResults]);

    useEffect(() => {
        setInvitations(_sortBy(invitesResults, ['email']));
    }, [invitesResults]);

    const removeUser = (userId: string) => {
        const filteredUsers = users.filter((user) => user.id !== userId);
        setUsers(filteredUsers);
    };

    const removeInvitation = (invitationId: string) => {
        const filteredUsers = invitations.filter(
            (invitation) => invitation.id !== invitationId,
        );
        setInvitations(filteredUsers);
    };

    const updateInvitations = (deletedInviteId: string, newInvite: Invitation[]) => {
        const filteredUsers = invitations.filter(
            (invitation) => invitation.id !== deletedInviteId,
        );
        setInvitations(_sortBy([...filteredUsers, ...newInvite], ['email']));
    };

    const getFilteredUserTable = (chips: string[]): FirestoreUser[] => {
        if (isEmpty(chips)) {
            return users;
        }

        const filteredUsers: FirestoreUser[] = [];
        users.forEach((user) => {
            chips.forEach((chip) => {
                if (
                    includes(
                        user.firstName.toLowerCase(),
                        chip.toLowerCase(),
                    ) ||
                    includes(user.lastName.toLowerCase(), chip.toLowerCase()) ||
                    includes(user.email.toLowerCase(), chip.toLowerCase())
                ) {
                    filteredUsers.push(user);
                }
            });
        });
        return filteredUsers;
    };

    const getFilteredInvitationsTable = (chips: string[]): Invitation[] => {
        if (isEmpty(chips)) {
            return invitations;
        }

        const filteredInvitations: Invitation[] = [];
        invitations.forEach((invitation) => {
            chips.forEach((chip) => {
                if (
                    includes(invitation.email.toLowerCase(), chip.toLowerCase())
                ) {
                    filteredInvitations.push(invitation);
                }
            });
        });
        return filteredInvitations;
    };

    const handleOnSearchBarChange = (chips: string[]) => {
        const filteredUsers = getFilteredUserTable(chips);
        const filteredInvitations = getFilteredInvitationsTable(chips);
        setSearchFilters(chips);
        setUsersTable(_sortBy(filteredUsers, ['displayName', 'email']));
        setInvitationsTable(_sortBy(filteredInvitations, ['email']));
    };

    const addInvitation = (newInvitation: Invitation[]) => {
        setInvitations((prev) => _sortBy([...prev, ...newInvitation], ['email']));
    };

    const addUser = (newUser: FirestoreUser) => {
        setUsers((prev) => _sortBy(Array.from(new Set([...prev, newUser])),['displayName', 'email']));
    };

    useEffect(() => {
        const usersFilteredBySearch = getFilteredUserTable(searchFilters);
        const invitationsFilteredBySearch =
            getFilteredInvitationsTable(searchFilters);

        setUsersTable(_sortBy(usersFilteredBySearch, ['displayName', 'email']));
        setInvitationsTable(_sortBy(invitationsFilteredBySearch, ['email']));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFilters]);

    return {
        removeUser,
        removeInvitation,
        updateInvitations,
        searchFilters,
        searchInput,
        setSearchInput,
        handleOnSearchBarChange,
        usersTable,
        invitationsTable,
        users,
        invitations,
        selectedInvitationType,
        setSelectedInvitationType,
        addInvitation,
        addUser,
        loadingUsers,
        loadingInvites,
    };
};

export default useUsersTable;
